import React, { useMemo, type JSX } from 'react';
import { analyticEvents } from './Components/TipInteractionButtonResources';
import Tip from './Tip';
import { StyledActionSlide } from './TipSlideshow.styled';

import { ENERGY_INSIGHTS } from '@/src/constants/analytics';

import { Slideshow } from '@/src/components/Slideshow/Slideshow';
import { UseGetTipInteractionsFromLocalStorage } from '@/src/pages/EnergyInsights/hooks/useGetTipInteractionsFromLocalStorage';
import { RandomTipsAllCategories } from '@/src/pages/EnergyInsights/types';
import { AnyBreakdownCatID } from '@/src/pages/EnergyInsights/types/common.types';
import { TipContent } from '@/src/pages/EnergyInsights/utils/tips';
import AnalyticsService from '@/src/services/analyticsService';
import useWindowDimensions from '@/src/utils/hooks/useWindowDimensions';

type TipSlideshowBaseProps = {
  tips: JSX.Element[];
  cards: number;
  category: AnyBreakdownCatID | RandomTipsAllCategories;
};

const TipSlideshow = ({ tips, cards, category }: TipSlideshowBaseProps) => {
  return (
    <Slideshow
      dataTestId={`ActionsSlideshow-cards-${cards}`}
      trackingOptions={{
        shiftLeft: () => {
          AnalyticsService.dispatch(
            ENERGY_INSIGHTS.spotlightsClickedShiftLeft(category),
          );
        },
        shiftRight: () => {
          AnalyticsService.dispatch(
            ENERGY_INSIGHTS.spotlightsClickedShiftRight(category),
          );
        },
      }}
    >
      {tips
        .map((tip, i) => {
          if (i % cards === 0) {
            const tipRight = tips[i + 1];
            return (
              <StyledActionSlide key={i}>
                {tip}
                {cards >= 2 ? tipRight : null}
              </StyledActionSlide>
            );
          }
          return null;
        })
        .filter(el => el !== null)}
    </Slideshow>
  );
};

export type TipSlideshowProps = {
  tips: Array<TipContent>;
  category: AnyBreakdownCatID | RandomTipsAllCategories;
  tipInteractions: UseGetTipInteractionsFromLocalStorage;
};

const TipSlideshowWrapper = ({
  tips,
  category,
  tipInteractions: { tipInteractionStatuses, setTipInteractionStatus },
}: TipSlideshowProps) => {
  const { isMobile } = useWindowDimensions();
  const TipComponents = useMemo(
    () =>
      tips.map((tip, index) => (
        <Tip
          key={index}
          {...tip}
          tipInteractionStatus={tipInteractionStatuses[tip.title]}
          handleUpdateTipInteractionStatus={tipTitle => interactionStatus => {
            AnalyticsService.dispatch(analyticEvents[interactionStatus], {
              tipTitle,
            });
            setTipInteractionStatus(tipTitle)(interactionStatus);
          }}
        />
      )),
    [tips, tipInteractionStatuses, setTipInteractionStatus],
  );

  return (
    <TipSlideshow
      tips={TipComponents}
      cards={isMobile ? 1 : 2}
      category={category}
    />
  );
};

export default TipSlideshowWrapper;
